import React from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { ListItemButton, ListItemIcon, Typography } from '@mui/material';
import ListSubheader from '@mui/material/ListSubheader';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import WebAssetIcon from '@mui/icons-material/WebAsset';

import { useAppModules } from '../../../hooks/useAppModules';
import LoadingSpinner from '../../common/controls/LoadingSpinner';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import { useDeleteSubModule } from '../../../hooks/useDeleteSubModule';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import ModuleForm from '../forms/ModuleForm';
import SubModuleForm from '../forms/SubModuleForm';

export default function AppModules() {
    const { t } = useTranslation('common');
    const [open, setOpen] = React.useState(false);

    const [trigger, setTrigger] = React.useState(false);
    // Toggle function
    const toggleTrigger = () => {
      setTrigger(prev => !prev);
    };

    const [moduleFormOpen, setModuleFormOpen] = React.useState(false);
    const [selectedModule, setSelectedModule] = React.useState(null);

    const [subModuleFormOpen, setSubModuleFormOpen] = React.useState(false);
    const [selectedSubModule, setSelectedSubModule] = React.useState(null);
    
    const [formMode, setFormMode] = React.useState("create");

    const { appModules, loading, setAppModules } = useAppModules(trigger);

    const onDeleteSuccess = () => {
      toggleTrigger();
    };
  
    const { remove } = useDeleteSubModule(onDeleteSuccess);
    
    const { showSnackbar } = useSnackbar();

    const handleClickOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const onSaveSuccess = () => {
      toggleTrigger();
    };

    const handleAddModule = () => {
        setSelectedModule(null);
        setFormMode("create");
        setModuleFormOpen(true);
    };

    const handleEditModule = (module) => {
        setSelectedModule(module);
        setFormMode("edit");
        setModuleFormOpen(true);
    };

    const handleAddSubModule = (module) => {
        setSelectedModule(module);
        setSelectedSubModule(null);
        setFormMode("create");
        setSubModuleFormOpen(true);
    };

    const handleEditSubModule = (module, subModule) => {
        setSelectedModule(module);
        setSelectedSubModule(subModule);
        setFormMode("edit");
        setSubModuleFormOpen(true);
    };

    const handleDeleteSubModule = async (subModule) => {
        try {
          await remove(subModule.id);
          showSnackbar(t('app_sub_modules.messages.delete_success', { name: subModule.name }), "success");
        } catch (err) {
          showSnackbar(t('app_sub_modules.messages.delete_fail', { name: subModule.name }), "error");
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <React.Fragment>
            <ListItem>
                <ListItemButton onClick={handleClickOpen} >
                    <ListItemIcon>
                        <WebAssetIcon fontSize='large' />
                    </ListItemIcon>

                    <ListItemText 
                        primary="App Modules"
                        secondary="view, add, edit, delete app modules"
                    >
                    </ListItemText>
                </ListItemButton>
            </ListItem>

            <Dialog onClose={handleClose} open={open} disableRestoreFocus>
                <DialogTitle>App Modules</DialogTitle>

                <DialogContent dividers>
                <List>
                  {appModules.map((module) => (
                    <React.Fragment key={module.id}>
                      <ListSubheader>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="h6" sx={{ flexGrow: 1 }}>
                            {`${module.sequence}. ${module.name}`}
                          </Typography>
                          <VerticalHamburgerMenu
                            options={[
                              { id: 1, name: 'Add SubModule', callback: () => handleAddSubModule(module) },
                              { id: 2, name: 'Edit', callback: () => handleEditModule(module) }
                            ]}
                          />
                        </Box>
                      </ListSubheader>

                      {module.subModules.map((subModule) => (
                        <ListItem key={subModule.id} sx={{ pl: 4, py: 0 }}>
                          <ListItemText
                            primary={
                              <Typography variant="body2">
                                {`${subModule.sequence}. ${subModule.name}`}
                              </Typography>
                            }
                            primaryTypographyProps={{
                              noWrap: true,
                              style: {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              },
                            }}
                          />
                          <Box sx={{ ml: 'auto' }}>
                            <VerticalHamburgerMenu
                              options={[
                                { id: 1, name: 'Edit', callback: () => handleEditSubModule(module, subModule) },
                                { id: 2, name: 'Delete', callback: () => handleDeleteSubModule(subModule),
                                  confirmMessage: t('app_sub_modules.messages.delete_confirm', { name: subModule.name }) },
                              ]}
                            />
                          </Box>
                        </ListItem>
                      ))}
                    </React.Fragment>
                  ))}
                </List>


                <ModuleForm
                  module={selectedModule}
                  successCallback={onSaveSuccess}
                  mode={formMode}
                  open={moduleFormOpen}
                  onClose={() => setModuleFormOpen(false)}
                />

                <SubModuleForm
                  module={selectedModule}
                  subModule={selectedSubModule}
                  successCallback={onSaveSuccess}
                  mode={formMode}
                  open={subModuleFormOpen}
                  onClose={() => setSubModuleFormOpen(false)}
                />

                </DialogContent>

                <DialogActions>
                <Button onClick={handleAddModule}>Add Module</Button>
                <div style={{ flex: '1 0 0' }} />
                <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}